import {hasValue} from '@famly/stat_ts-utils_has-value';
import {getBehaviorsCollectionFromGraphQL} from 'web-app/behaviors/behaviors-collection';
import InstitutionBehaviors from 'web-app/behaviors/institution-behavior-ids';
import * as FileUploadHelpers from 'web-app/react/components/file-upload/typed-helpers';
import type * as FileUploadTypes from 'web-app/react/components/file-upload/types';
import type FileRecord from 'web-app/react/components/file-upload/file-record';
import {ReplyStatus} from 'web-app/util/graphql/api-types';

import * as Constants from './constants';
import {type InstitutionsBehaviorsQuery, type MediaTaggedQueryVariables} from './__generated__/queries.api-types';
import {type ChildIds, type TaggedFile, type TaggedFiles} from './hook-reducer';
import type * as Types from './types';

const validTaggableMedia: FileUploadTypes.MediaType[] = Constants.TAGGABLE_MEDIA_TYPES;

export const dontForgetTranslation = (translation: string) => translation;

export const shouldFollowTaggingFlow = (institutionsBehaviors: InstitutionsBehaviorsQuery) =>
    institutionsBehaviors.institutions.some(institution =>
        getBehaviorsCollectionFromGraphQL(institution.behaviors).includes(InstitutionBehaviors.ShouldFollowTaggingFlow),
    );

export const isTaggableFile = (file: File) => {
    const mediaType = FileUploadHelpers.getFileMediaType(file);
    return validTaggableMedia.includes(mediaType);
};

export const getNextSelectedFileIndex = (
    selectedTaggedFileIndex: number | undefined,
    taggedFiles: TaggedFiles | undefined,
) => {
    if (!hasValue(selectedTaggedFileIndex) || !taggedFiles) {
        return undefined;
    }

    return (selectedTaggedFileIndex + 1) % taggedFiles.length;
};

export const getPreviousSelectedFileIndex = (
    selectedTaggedFileIndex: number | undefined,
    taggedFiles: TaggedFiles | undefined,
) => {
    if (!hasValue(selectedTaggedFileIndex) || !taggedFiles) {
        return undefined;
    }

    const previousSelectedFileIndex = selectedTaggedFileIndex - 1;

    return previousSelectedFileIndex < 0 ? taggedFiles.length - 1 : previousSelectedFileIndex;
};

export const getNextSelectedFileIndexAfterDiscarding = (
    selectedTaggedFileIndex: number | undefined,
    taggedFiles: TaggedFiles | undefined,
) => {
    if (!hasValue(selectedTaggedFileIndex) || !taggedFiles) {
        return undefined;
    }

    const newTaggedFilesLength = taggedFiles.length - 1;

    return Math.min(selectedTaggedFileIndex, newTaggedFilesLength - 1);
};

export const getPersistedObjectId = (file: FileUploadTypes.PersistedObject): string => {
    switch (file.mediaType) {
        case 'Image':
            return file.id ?? file.imageId;
        case 'Video':
            return file.id ?? file.videoId;
        case 'File':
        default:
            return file.id ?? file.fileId;
    }
};

export const getMediaTaggedVariables = (taggedFile: TaggedFiles[0] | undefined): MediaTaggedQueryVariables => {
    switch (taggedFile?.mediaType) {
        case 'Image':
            return {imageIds: [taggedFile.fileId]};
        case 'Video':
            return {videoIds: [taggedFile.fileId]};
        default:
            return {imageIds: undefined, videoIds: undefined};
    }
};

export const isLegalPersistedObject = (
    persistedObject: FileRecord['item'],
): persistedObject is FileUploadTypes.PersistedImage | FileUploadTypes.PersistedVideo => {
    return hasValue(persistedObject) && validTaggableMedia.includes(persistedObject.mediaType);
};

export const isFilePersisted = (
    fileItem: FileRecord['item'],
    file: FileRecord,
): fileItem is FileUploadTypes.PersistedObject => {
    // 1. If a file is persisted, it contains a permanent `fileId`, `imageId` or `videoId`.
    // Until it's persisted, the file's name is used as a temporary id.
    // 2. `uploadProgress` on the file itself is only updated when the upload begins, completes or fails.
    return (
        file.uploadProgress === null &&
        fileItem !== null &&
        (Object.prototype.hasOwnProperty.call(fileItem, 'fileId') ||
            Object.prototype.hasOwnProperty.call(fileItem, 'imageId') ||
            Object.prototype.hasOwnProperty.call(fileItem, 'videoId') ||
            Object.prototype.hasOwnProperty.call(fileItem, 'id'))
    );
};

export const hasTaggedChildren = (taggedChildren: TaggedFile['taggedChildren']): taggedChildren is ChildIds => {
    return Array.isArray(taggedChildren);
};

export const getCanBeTagged = (childAllowance: Types.ChildAllowance) => {
    return childAllowance.allowance === ReplyStatus.PERMITTED;
};
